import type {ReactNode} from 'react'
import {useEffect, useState} from 'react'
import type {NavigateFunction} from 'react-router'
import {useLocation, useNavigate} from 'react-router'
import isEqual from 'lodash/isEqual'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {useDistricts} from '@freckle/educator-entities/ts/common/hooks/use-districts'
import {useSchools} from '@freckle/educator-entities/ts/common/hooks/use-schools'
import type {DistrictAttrs} from '@freckle/educator-entities/ts/roster/models/district'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {logout} from '@freckle/school/ts/common/components/dashboard/logout'
import {WithResources} from '@freckle/school/ts/common/components/with-resources'
import Analytics from '@freckle/school/ts/common/helpers/analytics'
import ApiHelper from '@freckle/school/ts/common/helpers/api-helper'
import {logError} from '@freckle/school/ts/common/helpers/exception-handlers/bugsnag-client'
import {initalizePendo} from '@freckle/school/ts/common/routers/app-router'
import {setupRenaissanceKeepalive} from '@freckle/school/ts/common/routers/init'
import {useSchoolAdmin} from '@freckle/school/ts/hooks/use-school-admin'
import type {SchoolAdminAttrs} from '@freckle/school/ts/users/models/school-admin'
import {Routes} from './routes'

type Location = ReturnType<typeof useLocation>

export function Middleware({children}: {children: ReactNode}) {
  const navigate = useNavigate()

  useSafeEffectExtraDeps<{navigate: NavigateFunction}>(
    ({navigate}) => {
      ApiHelper.checkSessionCookies().then(
        () => {},
        () => {
          navigate(Routes.login())
        }
      )
    },
    [],
    {navigate: staticExtra(navigate)}
  )

  return <MiddlewareInner>{children}</MiddlewareInner>
}

function MiddlewareInner({children}: {children: ReactNode}) {
  const location = useLocation()
  const schools = useSchools()
  const districts = useDistricts()
  const schoolAdmin = useSchoolAdmin()
  return (
    <WithResources
      resources={{schools, districts, schoolAdmin}}
      render={({schools, districts, schoolAdmin}) => (
        <Inner
          location={location}
          schools={schools}
          districts={districts}
          schoolAdmin={schoolAdmin}
        >
          {children}
        </Inner>
      )}
    />
  )
}

type Props = {
  location: Location
  schools: SchoolAttrs[]
  districts: DistrictAttrs[]
  schoolAdmin: SchoolAdminAttrs
  children: ReactNode
}

function Inner(props: Props) {
  const {location, schools, districts, schoolAdmin, children} = props
  const [loading, setLoading] = useState<boolean>(true)

  useSafeEffectExtraDeps<{
    districts: DistrictAttrs[]
    schools: SchoolAttrs[]
    schoolAdmin: SchoolAdminAttrs
    location: Location
  }>(
    ({districts, schools, schoolAdmin, location}) => {
      const fetchData = async () => {
        try {
          await Analytics.trackSchoolRouteChanges(location)
          setupRenaissanceKeepalive(schoolAdmin)

          initalizePendo(schoolAdmin, districts, schools)

          setLoading(false)
        } catch (error) {
          logError(error as Error)
          setLoading(false)
          logout()
        }
      }

      fetchData()
    },
    [],
    {
      districts: {value: districts, comparator: isEqual},
      schools: {value: schools, comparator: isEqual},
      schoolAdmin: {value: schoolAdmin, comparator: isEqual},
      location: {value: location, comparator: isEqual}
    }
  )

  useEffect(() => {
    Analytics.trackSchoolRouteChanges(location)
  }, [location])

  if (loading || children === null || children === undefined) {
    return <></>
  }
  return <>{children}</>
}
